import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FadeIn } from '../components/ui/FadeIn';
import { User, Heart, Users, Shield, Brain, ArrowRight, CheckCircle2 } from 'lucide-react';
const SERVICES = [{
  id: 'individual',
  title: 'Individual Counselling & Therapy',
  description: 'Personalized one-on-one therapy sessions to help you work through PTSD, anxiety, depression, grief, and life transitions. Together, we create a safe space for self-exploration and healing.',
  icon: User,
  features: ['PTSD & Trauma Recovery', 'Anxiety & Depression', 'Grief & Loss', 'Life Transitions', 'Self-Esteem & Identity']
}, {
  id: 'couples',
  title: 'Couples Therapy',
  description: 'Strengthen your relationship through improved communication, conflict resolution, and deeper emotional connection. Rebuild trust and rediscover your partnership.',
  icon: Heart,
  features: ['Communication Skills', 'Conflict Resolution', 'Trust Rebuilding', 'Emotional Intimacy', 'Pre-Marital Counselling']
}, {
  id: 'family',
  title: 'Family Therapy',
  description: 'Address family dynamics, improve communication patterns, and heal relationships. Navigate parenting challenges and strengthen family bonds.',
  icon: Users,
  features: ['Family Dynamics', 'Parenting Challenges', 'Blended Families', 'Adolescent Issues', 'Intergenerational Patterns']
}, {
  id: 'trauma',
  title: 'Trauma Therapy',
  description: 'Evidence-based trauma-focused therapy to help you process and heal from traumatic experiences and reduce PTSD symptoms using proven clinical methods.',
  icon: Shield,
  features: ['PTSD Treatment', 'Complex Trauma', 'Childhood Trauma', 'Accident Recovery', 'Trauma-Informed Care']
}, {
  id: 'emdr',
  title: 'EMDR Therapy',
  description: 'Eye Movement Desensitization and Reprocessing — a powerful, evidence-based therapy that helps the brain process and heal from disturbing memories and experiences.',
  icon: Brain,
  features: ['Memory Reprocessing', 'Anxiety Reduction', 'Phobia Treatment', 'Performance Enhancement', 'Grief Processing']
}];
export function ServicesPage() {
  return <>
      {/* Hero */}
      <section className="bg-olive py-16 md:py-20" data-id="element-1109">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1110">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center max-w-3xl mx-auto" data-id="element-1111">
            <span className="text-gold font-bold uppercase tracking-widest text-xs mb-4 block" data-id="element-1112">
              OUR SERVICES
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-id="element-1113">
              Counselling & Therapy{' '}
              <span className="italic text-cream" data-id="element-1114">Services</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg font-medium max-w-2xl mx-auto" data-id="element-1115">
              Comprehensive therapeutic support tailored to your unique needs.
              Every journey is different — find the right path for you.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-20 bg-cream" data-id="element-1116">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1117">
          <div className="space-y-6" data-id="element-1118">
            {SERVICES.map((service, index) => {
            const Icon = service.icon;
            const isEven = index % 2 === 0;
            return <FadeIn key={service.id} delay={index * 0.1} data-id="element-1119">
                  <div className={`bg-white rounded-2xl overflow-hidden border border-charcoal/5 shadow-soft ${isEven ? '' : ''}`} data-id="element-1120">
                    <div className="p-6 md:p-8 lg:p-10" data-id="element-1121">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-10" data-id="element-1122">
                        <div className="flex-shrink-0" data-id="element-1123">
                          <div className="w-16 h-16 rounded-2xl bg-olive/10 flex items-center justify-center" data-id="element-1124">
                            <Icon size={32} className="text-olive" strokeWidth={1.5} data-id="element-1125" />
                          </div>
                        </div>
                        <div className="flex-grow" data-id="element-1126">
                          <h2 className="font-heading text-xl md:text-2xl font-bold text-charcoal mb-3" data-id="element-1127">
                            {service.title}
                          </h2>
                          <p className="text-charcoal/70 text-base font-medium leading-relaxed mb-4" data-id="element-1128">
                            {service.description}
                          </p>
                          <div className="flex flex-wrap gap-2 mb-4" data-id="element-1129">
                            {service.features.map(f => <span key={f} className="inline-flex items-center gap-1.5 text-sm font-medium text-olive bg-olive/5 px-3 py-1 rounded-full" data-id="element-1130">
                                <CheckCircle2 size={14} data-id="element-1131" />
                                {f}
                              </span>)}
                          </div>
                        </div>
                        <div className="flex-shrink-0" data-id="element-1132">
                          <Link to={`/services/${service.id}`} className="inline-flex items-center justify-center bg-olive hover:bg-olive/90 text-white px-6 py-3 rounded-full font-bold text-sm transition-all shadow-soft min-h-[44px] tracking-wide whitespace-nowrap" data-id="element-1133">
                            Learn More
                            <ArrowRight size={16} className="ml-2" data-id="element-1134" />
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </FadeIn>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-olive" data-id="element-1135">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1136">
          <FadeIn data-id="element-1137">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1138">
              Not Sure Which Service Is Right for You?
            </h2>
            <p className="text-white/80 text-base font-medium mb-8 max-w-xl mx-auto" data-id="element-1139">
              Book a free 15-minute consultation and we'll discuss which
              approach would be most beneficial. No referral required.
            </p>
            <a href="https://esthermensah.janeapp.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-gold hover:bg-gold/90 text-white px-8 py-3.5 rounded-full font-bold text-base transition-all shadow-lg min-h-[48px] uppercase tracking-wide" data-id="element-1140">
              Book a Free Consultation
            </a>
          </FadeIn>
        </div>
      </section>
    </>;
}